import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, TrendingDown, DollarSign, PiggyBank } from 'lucide-react';
import { analyticsApi, bankAccountApi } from '../services/api';
import { AnalyticsSummary, CashflowItem, BankAccount } from '../types';
import PeriodInput, { periodToStartIso, periodToEndIso, isoToPeriod } from '../components/PeriodInput';
import Alert from '../components/Alert';
import PageSuggestion from '../components/PageSuggestion';

const Analytics: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
    const [cashflow, setCashflow] = useState<CashflowItem[]>([]);
    const [cashflowLoading, setCashflowLoading] = useState<boolean>(false);
    const [cashflowError, setCashflowError] = useState<string | null>(null);
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [selectedBankAccountId, setSelectedBankAccountId] = useState<string>('');
    const [groupBy, setGroupBy] = useState<'day' | 'month'>('month');

    const defaultPeriod = useMemo(() => isoToPeriod(null), []);
    const [filterStartPeriod, setFilterStartPeriod] = useState<string>(defaultPeriod);
    const [filterEndPeriod, setFilterEndPeriod] = useState<string>(defaultPeriod);

    const fetchSummary = async () => {
        setLoading(true);
        setError(null);
        try {
            const startIso = periodToStartIso(filterStartPeriod);
            const endIso = periodToEndIso(filterEndPeriod);
            const startDate = startIso.split('T')[0];
            const endDate = endIso.split('T')[0];

            const response = await analyticsApi.getSummary({ startDate, endDate });
            setSummary(response.data);
        } catch (err: any) {
            setError('Erro ao carregar análises. Por favor, tente novamente.');
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSummary();
        fetchBankAccounts();
        fetchCashflow();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchCashflow();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBankAccountId, groupBy]);

    const fetchBankAccounts = async () => {
        try {
            const response = await bankAccountApi.getAll();
            const accounts = response.data || [];
            setBankAccounts(accounts);
            // if there's no selection yet, default to the first account to ensure bankAccountId is sent
            if (!selectedBankAccountId && accounts.length > 0) {
                setSelectedBankAccountId(accounts[0].id);
            }
        } catch (err) {
            // Silently fail - bank accounts are optional
        }
    };

    const fetchCashflow = async () => {
        setCashflowLoading(true);
        setCashflowError(null);
        try {
            const startIso = periodToStartIso(filterStartPeriod);
            const endIso = periodToEndIso(filterEndPeriod);
            const startDate = startIso.split('T')[0];
            const endDate = endIso.split('T')[0];

            // Always try to send a bankAccountId when possible
            const effectiveBankAccountId = selectedBankAccountId || (bankAccounts.length > 0 ? bankAccounts[0].id : undefined);
            const params: { bankAccountId?: string; groupBy: 'day' | 'month'; startDate: string; endDate: string } = {
                groupBy,
                startDate,
                endDate,
                ...(effectiveBankAccountId ? { bankAccountId: effectiveBankAccountId } : {}),
            };

            const response = await analyticsApi.getCashflow(params);
            const raw = (response.data as any[]) || [];
            const normalized: CashflowItem[] = raw.map((it: any) => {
                const d = new Date(it.period);
                const yyyy = d.getUTCFullYear();
                const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
                const dd = String(d.getUTCDate()).padStart(2, '0');
                const periodStr = groupBy === 'month' ? `${yyyy}-${mm}` : `${yyyy}-${mm}-${dd}`;
                const inflow = (it.inflow ?? it.inflows ?? 0) as number;
                const outflow = (it.outflow ?? it.outflows ?? 0) as number;
                const net = (it.net ?? (inflow - outflow)) as number;
                return { period: periodStr, inflow, outflow, net };
            });
            setCashflow(normalized);
        } catch (err: any) {
            setCashflowError('Erro ao carregar fluxo de caixa.');
            setCashflow([]);
        } finally {
            setCashflowLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-secondary-900">Análises Financeiras</h1>
                <p className="text-secondary-600">Visão geral do período com resultado líquido</p>
                <PageSuggestion>
                    Use esta tela para comparar entradas e saídas por período e identificar rapidamente onde o caixa está ganhando ou perdendo força.
                </PageSuggestion>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg border border-secondary-200">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <PeriodInput
                            label="Data inicial (MM/AAAA)"
                            name="startDate"
                            value={filterStartPeriod}
                            onChange={(v) => {
                                setFilterStartPeriod(v);
                                setFilterEndPeriod(v);
                            }}
                            compact
                        />
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <PeriodInput
                            label="Data final (MM/AAAA)"
                            name="endDate"
                            value={filterEndPeriod}
                            onChange={setFilterEndPeriod}
                            compact
                        />
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[160px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Conta Bancária</label>
                        <select
                            className="input-field"
                            value={selectedBankAccountId}
                            onChange={(e) => setSelectedBankAccountId(e.target.value)}
                        >
                            <option value="">Todas</option>
                            {bankAccounts.map(account => (
                                <option key={account.id} value={account.id}>
                                    {account.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Agrupar Por</label>
                        <select
                            className="input-field"
                            value={groupBy}
                            onChange={(e) => setGroupBy(e.target.value as 'day' | 'month')}
                        >
                            <option value="month">Mensal</option>
                            <option value="day">Diário</option>
                        </select>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            fetchSummary();
                            fetchCashflow();
                        }}
                        className="btn-primary w-full sm:w-auto shrink-0 h-[42px]"
                        disabled={loading || cashflowLoading}
                    >
                        {loading || cashflowLoading ? 'Carregando...' : 'Filtrar'}
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (<Alert type="error" title="Não foi possível carregar" message={error} />)}

            {/* Loading State */}
            {loading && !summary && (
                <div className="flex items-center justify-center h-64">
                    <div className="text-secondary-600">Carregando análises...</div>
                </div>
            )}

            {/* Summary Cards */}
            {summary && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Total a Pagar */}
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm font-medium text-red-800">Total a Pagar</div>
                                    <div className="mt-2 text-xl sm:text-2xl font-bold text-red-900 break-words">
                                        {(summary.totalPayables / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </div>
                                </div>
                                <TrendingDown className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="mt-4 pt-4 border-t border-red-200">
                                <div className="text-xs text-red-700">Taxa de Pagamento</div>
                                <div className="mt-1 text-lg font-semibold text-red-900">{summary.paidPercentage}%</div>
                            </div>
                        </div>

                        {/* Total a Receber */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm font-medium text-green-800">Total a Receber</div>
                                    <div className="mt-2 text-xl sm:text-2xl font-bold text-green-900 break-words">
                                        {(summary.totalReceivables / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </div>
                                </div>
                                <TrendingUp className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="mt-4 pt-4 border-t border-green-200">
                                <div className="text-xs text-green-700">Taxa de Recebimento</div>
                                <div className="mt-1 text-lg font-semibold text-green-900">{summary.receivedPercentage}%</div>
                            </div>
                        </div>

                        {/* Resultado Líquido */}
                        <div className={`border rounded-lg p-6 ${summary.netResult >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className={`text-sm font-medium ${summary.netResult >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>
                                        Resultado Líquido
                                    </div>
                                    <div className={`mt-2 text-xl sm:text-2xl font-bold break-words ${summary.netResult >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
                                        {(summary.netResult / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </div>
                                </div>
                                <DollarSign className={`h-8 w-8 ${summary.netResult >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
                            </div>
                            <div className={`mt-4 pt-4 border-t ${summary.netResult >= 0 ? 'border-blue-200' : 'border-orange-200'}`}>
                                <div className={`text-xs ${summary.netResult >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                                    {summary.netResult >= 0 ? 'Superávit' : 'Déficit'}
                                </div>
                            </div>
                        </div>

                        {/* Total investido em caixinhas */}
                        <div className="bg-violet-50 border border-violet-200 rounded-lg p-6">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm font-medium text-violet-800">Caixinhas (investido)</div>
                                    <div className="mt-2 text-xl sm:text-2xl font-bold text-violet-900 break-words">
                                        {((summary.totalInvestments ?? 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </div>
                                </div>
                                <PiggyBank className="h-8 w-8 text-violet-600 shrink-0" />
                            </div>
                            <div className="mt-4 pt-4 border-t border-violet-200">
                                <div className="text-xs text-violet-700">Total investido nas caixinhas até o momento</div>
                            </div>
                        </div>

                        {/*
                        Total Final (summary.finalTotal) — oculto por enquanto; restaurar o bloco abaixo e import BarChart3 se precisar exibir de novo.
                        {typeof summary.finalTotal === 'number' && (
                            <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-medium text-primary-800">Total Final</div>
                                        <div className="mt-2 text-2xl font-bold text-primary-900">
                                            {(summary.finalTotal / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                        </div>
                                    </div>
                                    <BarChart3 className="h-8 w-8 text-primary-600" />
                                </div>
                                <div className="mt-4 pt-4 border-t border-primary-200">
                                    <div className="text-xs text-primary-700">Resultado líquido + investimentos</div>
                                </div>
                            </div>
                        )}
                        */}
                    </div>
                </>
            )}

            {/* Cashflow Controls */}
            <div className="bg-white border border-secondary-200 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <div>
                        <h2 className="text-lg font-semibold text-secondary-900">Fluxo de Caixa</h2>
                        <p className="text-sm text-secondary-600">Agrupado por {groupBy === 'month' ? 'mês' : 'dia'}</p>
                    </div>
                    <div className="inline-flex rounded-md shadow-sm w-full sm:w-auto" role="group">
                        <button
                            type="button"
                            className={`flex-1 sm:flex-none px-3 py-1.5 text-sm border border-secondary-300 ${groupBy === 'month' ? 'bg-primary-600 text-white' : 'bg-white text-secondary-800'} rounded-l-md`}
                            onClick={() => setGroupBy('month')}
                            aria-pressed={groupBy === 'month'}
                        >
                            Mensal
                        </button>
                        <button
                            type="button"
                            className={`flex-1 sm:flex-none px-3 py-1.5 text-sm border-t border-b border-secondary-300 ${groupBy === 'day' ? 'bg-primary-600 text-white' : 'bg-white text-secondary-800'} rounded-r-md`}
                            onClick={() => setGroupBy('day')}
                            aria-pressed={groupBy === 'day'}
                        >
                            Diário
                        </button>
                    </div>
                </div>
            <CashflowChart
                data={cashflow}
                loading={cashflowLoading}
                error={cashflowError}
                groupBy={groupBy}
            />
            </div>

            {/* Empty State */}
            {!loading && !summary && !error && (
                <div className="bg-white border border-secondary-200 rounded-lg p-8 text-center">
                    <p className="text-secondary-600">Selecione um período e clique em &quot;Filtrar&quot; para ver as análises.</p>
                </div>
            )}
        </div>
    );
};

// Cashflow Chart Component
interface CashflowChartProps {
    data: CashflowItem[];
    loading: boolean;
    error: string | null;
    groupBy: 'day' | 'month';
}

const CashflowChart: React.FC<CashflowChartProps> = ({ data, loading, error, groupBy }) => {
    const chartData = useMemo(() => {
        if (!data || data.length === 0) return null;

        const chartWidth = 900;
        const chartHeight = 400;
        const padding = { top: 40, right: 40, bottom: 60, left: 80 };
        const barWidth = Math.max(20, (chartWidth - padding.left - padding.right) / (data.length * 3 + (data.length - 1) * 1));
        const gap = barWidth * 0.5;

        // Calculate max value for scaling (values are in cents)
        const maxInflow = Math.max(...data.map(d => d.inflow), 0);
        const maxOutflow = Math.max(...data.map(d => d.outflow), 0);
        const maxNet = Math.max(...data.map(d => Math.abs(d.net)), 0);
        const maxValue = Math.max(maxInflow, maxOutflow, maxNet);

        const scale = (chartHeight - padding.top - padding.bottom) / (maxValue || 1);

        // Format period labels
        const formatPeriod = (period: string) => {
            if (groupBy === 'month') {
                // "2025-01" -> "Jan/2025"
                const [year, month] = period.split('-');
                const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                return `${monthNames[parseInt(month) - 1]}/${year}`;
            } else {
                // "2025-01-15" -> "15/01"
                const [year, month, day] = period.split('-');
                return `${day}/${month}`;
            }
        };

        return {
            chartWidth,
            chartHeight,
            padding,
            barWidth,
            gap,
            scale,
            maxValue,
            formatPeriod,
        };
    }, [data, groupBy]);

    if (loading) {
        return (
            <div className="bg-white border border-secondary-200 rounded-lg p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-secondary-600">Carregando fluxo de caixa...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white border border-secondary-200 rounded-lg p-6">
                <Alert type="error" title="Não foi possível carregar" message={error} />
            </div>
        );
    }

    if (!chartData || data.length === 0) {
        return (
            <div className="bg-white border border-secondary-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-secondary-900 mb-4">Fluxo de Caixa</h2>
                <p className="text-secondary-600">Nenhum dado de fluxo de caixa disponível para o período selecionado.</p>
            </div>
        );
    }

    const { chartWidth, chartHeight, padding, barWidth, gap, scale, maxValue, formatPeriod } = chartData;

    return (
        <div className="bg-white border border-secondary-200 rounded-lg p-6">
            <div className="mb-4">
                <h2 className="text-lg font-semibold text-secondary-900">Fluxo de Caixa</h2>
                <p className="text-sm text-secondary-600">Evolução de entradas, saídas e resultado líquido</p>
            </div>

            <div className="overflow-x-auto">
                <svg width={chartWidth} height={chartHeight} className="min-w-[900px]">
                    {/* Grid lines */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                        const y = padding.top + (1 - ratio) * (chartHeight - padding.top - padding.bottom);
                        const value = maxValue * ratio;
                        return (
                            <g key={ratio}>
                                <line
                                    x1={padding.left}
                                    y1={y}
                                    x2={chartWidth - padding.right}
                                    y2={y}
                                    stroke="#e5e7eb"
                                    strokeWidth={1}
                                    strokeDasharray="4 4"
                                />
                                <text
                                    x={padding.left - 10}
                                    y={y + 4}
                                    textAnchor="end"
                                    fontSize="10"
                                    fill="#6b7280"
                                >
                                    {(value / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
                                </text>
                            </g>
                        );
                    })}

                    {/* Bars */}
                    {data.map((item, index) => {
                        const x = padding.left + index * (barWidth * 3 + gap);
                        const inflowHeight = item.inflow * scale;
                        const outflowHeight = item.outflow * scale;
                        const netHeight = Math.abs(item.net * scale);

                        return (
                            <g key={index}>
                                {/* Inflow (green) */}
                                <rect
                                    x={x}
                                    y={padding.top + (chartHeight - padding.top - padding.bottom) - inflowHeight}
                                    width={barWidth}
                                    height={inflowHeight}
                                    fill="#10b981"
                                    opacity={0.8}
                                >
                                    <title>
                                        Entrada: {(item.inflow / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </title>
                                </rect>

                                {/* Outflow (red) */}
                                <rect
                                    x={x + barWidth + gap / 3}
                                    y={padding.top + (chartHeight - padding.top - padding.bottom) - outflowHeight}
                                    width={barWidth}
                                    height={outflowHeight}
                                    fill="#ef4444"
                                    opacity={0.8}
                                >
                                    <title>
                                        Saída: {(item.outflow / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </title>
                                </rect>

                                {/* Net (blue/orange) */}
                                <rect
                                    x={x + (barWidth + gap / 3) * 2}
                                    y={item.net >= 0
                                        ? padding.top + (chartHeight - padding.top - padding.bottom) - netHeight
                                        : padding.top + (chartHeight - padding.top - padding.bottom)
                                    }
                                    width={barWidth}
                                    height={netHeight}
                                    fill={item.net >= 0 ? "#3b82f6" : "#f97316"}
                                    opacity={0.8}
                                >
                                    <title>
                                        Líquido: {(item.net / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </title>
                                </rect>

                                {/* Period label */}
                                <text
                                    x={x + (barWidth * 3 + gap) / 2}
                                    y={chartHeight - padding.bottom + 20}
                                    textAnchor="middle"
                                    fontSize="10"
                                    fill="#6b7280"
                                >
                                    {formatPeriod(item.period)}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded" style={{ opacity: 0.8 }}></div>
                    <span className="text-secondary-700">Entradas</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500 rounded" style={{ opacity: 0.8 }}></div>
                    <span className="text-secondary-700">Saídas</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded" style={{ opacity: 0.8 }}></div>
                    <span className="text-secondary-700">Líquido (Positivo)</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-500 rounded" style={{ opacity: 0.8 }}></div>
                    <span className="text-secondary-700">Líquido (Negativo)</span>
                </div>
            </div>
        </div>
    );
};

export default Analytics;
